import { useEffect, useState, type ReactNode } from 'react';

import { Button } from '@/components/design-system/Button';
import { useDebounce } from '@/hooks/useDebounce';
import { SaveLayoutButton } from '@/modules/PMS/features/grid-layout';
import { ExpandAltOutlined, ShrinkOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { ColumnPicker, type ColumnDefinition } from './ColumnPicker';
import { GridSearchInput } from './GridSearchInput';

interface GridToolbarProps {
  isTree?: boolean;
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onVisibilityChange: (visibleColumnIds: string[]) => void;
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
  onQuickFilterChange: (quickFilterText: string) => void;
  toolbarActions?: ReactNode;
  filterButton?: ReactNode;
  showSearchBar?: boolean;
  showColumnPicker?: boolean;
  showExpandCollapse?: boolean;
  showToolbarLabels?: boolean;
  showSaveLayout?: boolean;
  onSaveLayout?: () => void;
  isSavingLayout?: boolean;
}

export function GridToolbar({
  isTree = false,
  columns,
  visibleColumns,
  onVisibilityChange,
  onExpandAll,
  onCollapseAll,
  onQuickFilterChange,
  toolbarActions,
  filterButton,
  showSearchBar = true,
  showColumnPicker = true,
  showExpandCollapse = true,
  showToolbarLabels = true,
  showSaveLayout = true,
  onSaveLayout,
  isSavingLayout = false
}: GridToolbarProps) {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 300);
  const quickFilterText = debouncedSearchText.length >= 2 ? debouncedSearchText : '';

  useEffect(() => {
    onQuickFilterChange(quickFilterText);
  }, [quickFilterText, onQuickFilterChange]);

  const handleSearchChange = (value: string) => {
    setSearchText(value);
  };

  return (
    <div className="bg-background flex shrink-0 items-center gap-2 py-2">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {showSearchBar && (
          <GridSearchInput value={searchText} onChange={handleSearchChange} className="max-w-96 min-w-0 flex-1" />
        )}
        {filterButton}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {showColumnPicker && (
          <ColumnPicker
            columns={columns}
            visibleColumns={visibleColumns}
            onVisibilityChange={onVisibilityChange}
            showLabel={showToolbarLabels}
            data-testid={isTree ? 'tree-grid-column-picker' : 'data-grid-column-picker'}
          />
        )}
        {isTree && showExpandCollapse && (
          <>
            <Button
              variant="default"
              size="sm"
              className="border-border-100"
              onClick={onExpandAll}
              title={!showToolbarLabels ? t('actions.expandAll') : undefined}
              data-testid="tree-grid-expand-all"
            >
              <ExpandAltOutlined />
              {showToolbarLabels && t('actions.expandAll')}
            </Button>
            <Button
              variant="default"
              size="sm"
              className="border-border-100"
              onClick={onCollapseAll}
              title={!showToolbarLabels ? t('actions.collapseAll') : undefined}
              data-testid="tree-grid-collapse-all"
            >
              <ShrinkOutlined />
              {showToolbarLabels && t('actions.collapseAll')}
            </Button>
          </>
        )}
        {showSaveLayout && onSaveLayout && (
          <SaveLayoutButton
            onSave={onSaveLayout}
            isSaving={isSavingLayout}
            data-testid={isTree ? 'tree-grid-save-layout' : 'data-grid-save-layout'}
          />
        )}
        {toolbarActions}
      </div>
    </div>
  );
}
